import { useRef, useState } from 'react';
import type React from 'react';
import { BusinessCard } from './BusinessCard.js';
import { CsvImport } from './CsvImport.js';
import { Alert } from './ui.js';
import { useConnectionsQuery } from './api.js';
import { CameraGlyph, MailIcon, SheetIcon, TrayIcon, ChevronRight } from './icons.js';
import { sourceStates, type SourceState } from './setupModel.js';

/**
 * "I have contacts I want to add."
 *
 * Four ways in, each on its own card with its live status: business cards, Gmail, Outlook
 * and CSV. The camera is still the primary action because it is the one somebody does
 * standing up, holding a card, at an event — everything else can afford an extra tap.
 *
 * Email is never a button that starts something. Mail is captured in the mailbox itself,
 * by applying one of the customer's capture labels or categories — so the Gmail and
 * Outlook cards say where that happens and link to the real places to act (connect, manage
 * labels, see what was captured), and nothing else.
 *
 * Purely intake. What happened to things already captured — their progress, their
 * outcome, their history — is Review's job, so there is no lifecycle list here.
 */

type Mode = 'menu' | 'card' | 'csv';

/**
 * A camera, unmistakably.
 *
 * Inline SVG rather than a dependency: this is the only icon the product needs so far,
 * and an icon library for one glyph is a lot of bytes on a phone. `aria-hidden` because
 * the button already says "Take a photo" — the icon adds recognition, not information.
 */
function CameraIcon() {
  return (
    <svg
      aria-hidden="true" focusable="false" width="22" height="22" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    >
      <path d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2.2a1 1 0 0 0 .83-.45l.94-1.4A1 1 0 0 1 9.3 4.7h5.4a1 1 0 0 1 .83.45l.94 1.4a1 1 0 0 0 .83.45h2.2A1.5 1.5 0 0 1 21 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5z" />
      <circle cx="12" cy="13" r="3.4" />
    </svg>
  );
}

interface Source {
  readonly key: string;
  readonly label: string;
  readonly hint: string;
  readonly ready: boolean;
}

/**
 * Two secondary actions, not three.
 *
 * "Photo of a list" was a peer action and should not have been. A photographed contact
 * list is an IMAGE, arriving the same two ways every other image arrives — the camera, or
 * the library. Making it a fourth button asked the customer to classify their own
 * photograph before taking it, which is a question the product should answer, not ask.
 *
 * What that leaves is one axis per acquisition method: camera, library, file. Typing a
 * contact in is not offered, because it does not exist — a disabled placeholder for it
 * described the roadmap rather than the product.
 */
const SECONDARY: readonly Source[] = [
  { key: 'photos', label: 'Choose photos', hint: 'One or several from your library', ready: true },
  { key: 'file', label: 'Upload file', hint: 'CSV contact list', ready: true },
];

export function Capture({ onOpenCrm, onOpenReview, onOpenCaptureSettings, onOpenConnections }: {
  onOpenCrm: () => void;
  /** Review, filtered to the source that was just captured. */
  onOpenReview?: (source: 'business_card' | 'import' | 'email') => void;
  onOpenCaptureSettings?: () => void;
  onOpenConnections?: () => void;
}) {
  const [mode, setMode] = useState<Mode>('menu');
  const [notice, setNotice] = useState<string | null>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const { data: conn } = useConnectionsQuery();
  const sources = sourceStates(conn?.connectors);

  if (mode === 'csv') {
    return (
      <div className="space-y-4">
        <BackLink onClick={() => setMode('menu')} />
        <CsvImport
          onClose={() => setMode('menu')}
          onViewInReview={onOpenReview ? () => onOpenReview('import') : undefined}
        />
      </div>
    );
  }

  if (mode === 'card') {
    return (
      <div className="space-y-4">
        <BackLink onClick={() => setMode('menu')} />
        {/* The proven workflow, reused rather than rebuilt. */}
        <BusinessCard
          onOpenCrm={onOpenCrm}
          onViewInReview={onOpenReview ? () => onOpenReview('business_card') : undefined}
        />
      </div>
    );
  }

  const photos = SECONDARY.find((s) => s.key === 'photos')!;
  const file = SECONDARY.find((s) => s.key === 'file')!;
  const pick = (s: Source) => {
    if (s.key === 'photos') { setMode('card'); return; }
    // The permanent entry point, now live. There is deliberately no other
    // way into CSV import anywhere in the product.
    if (s.key === 'file') { setMode('csv'); return; }
    setNotice(`${s.label} is not available yet.`);
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Capture contacts</h1>
        <p className="mt-1 text-sm text-slate-600">Add contacts to your CRM. Everything you capture waits in Review until you approve it.</p>
      </header>

      <ul className="grid gap-4 md:grid-cols-2">
        {/* ── Business cards: the primary action ─────────────────────── */}
        <li>
          <SourceCard
            icon={<CameraGlyph size={24} />} iconClass="bg-cp-sky text-cp-blue-600"
            title="Scan business cards" state={sources.card}
            body="Photograph a card and ContactPilot reads the name, title, company, phones, email and address for you to check."
          >
            {/*
              One obviously primary action. Large enough to hit while holding a card in the
              other hand.
            */}
            <button
              type="button"
              onClick={() => setMode('card')}
              className="flex min-h-11 w-full items-center justify-center gap-3 rounded-xl bg-cp-blue-600 px-5 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-cp-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-blue-600 sm:w-auto"
            >
              <CameraIcon />
              Take a photo
            </button>
            <SecondaryButton source={photos} onPick={pick} />
            {/*
              Source-neutral, and honest about what works today. The permanent UI should not
              read as business-card-only, but it must not promise list extraction either.
            */}
            <p className="w-full text-xs text-slate-500">Capture contact information with your camera.</p>
          </SourceCard>
        </li>

        {/* ── Gmail ──────────────────────────────────────────────────── */}
        <li>
          <SourceCard
            icon={<MailIcon size={24} />} iconClass="bg-rose-50 text-rose-700"
            title="From Gmail" state={sources.gmail}
            body="Apply your capture label to an email in Gmail. ContactPilot reads only the conversations you mark."
          >
            <MailboxActions
              connected={sources.gmail.status === 'Connected'}
              connectLabel="Connect Gmail"
              onOpenConnections={onOpenConnections}
              onOpenReview={onOpenReview ? () => onOpenReview('email') : undefined}
              onOpenCaptureSettings={onOpenCaptureSettings}
            />
          </SourceCard>
        </li>

        {/* ── Outlook ────────────────────────────────────────────────── */}
        <li>
          <SourceCard
            icon={<TrayIcon size={24} />} iconClass="bg-cp-blue-50 text-cp-blue-700"
            title="From Outlook" state={sources.outlook}
            body="Apply your capture category to an email in Outlook. ContactPilot reads only the conversations you mark."
          >
            <MailboxActions
              connected={sources.outlook.status === 'Connected'}
              connectLabel="Connect Outlook"
              onOpenConnections={onOpenConnections}
              onOpenReview={onOpenReview ? () => onOpenReview('email') : undefined}
              onOpenCaptureSettings={onOpenCaptureSettings}
            />
          </SourceCard>
        </li>

        {/* ── CSV ────────────────────────────────────────────────────── */}
        <li>
          <SourceCard
            icon={<SheetIcon size={24} />} iconClass="bg-cp-mint text-cp-teal-ink"
            title="Import CSV" state={sources.csv}
            body="Upload a spreadsheet of contacts. You match the columns, check the rows, and approve what goes to Keap."
          >
            <SecondaryButton source={file} onPick={pick} />
          </SourceCard>
        </li>
      </ul>

      {notice ? <Alert tone="info">{notice}</Alert> : null}
      {/* A hidden camera input is kept mounted so the hero can grow a direct-capture
          path later without restructuring the page. */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" />

      {/*
        Where email capture happens, said once. Not a button: there is nothing to start
        from here, and the marking itself is done in the customer's own mailbox.
      */}
      <div className="rounded-card border border-cp-border bg-white/70 p-4 text-sm text-slate-600" data-email-guidance="">
        <p>
          Capture emails by applying one of your capture labels in Gmail or categories in Outlook.
        </p>
        {onOpenCaptureSettings ? (
          <button
            type="button"
            onClick={onOpenCaptureSettings}
            className="inline-flex min-h-11 items-center font-semibold text-cp-blue-600 underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-blue-600"
          >
            Manage email capture
          </button>
        ) : null}
      </div>
    </div>
  );
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex min-h-11 items-center text-sm font-medium text-cp-blue-600 underline"
    >
      ← Back to Capture
    </button>
  );
}

const TONE: Record<SourceState['tone'], string> = {
  ready: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20',
  action: 'bg-amber-50 text-amber-900 ring-amber-600/25',
  idle: 'bg-slate-100 text-slate-600 ring-slate-500/20',
};

function SourceCard({ icon, iconClass, title, state, body, children }: {
  icon: React.ReactNode; iconClass: string; title: string; state: SourceState; body: string; children: React.ReactNode;
}) {
  return (
    <section
      aria-label={title}
      className="flex h-full flex-col rounded-card border border-cp-border bg-white p-5 shadow-card"
    >
      <div className="flex items-start gap-3">
        <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl ${iconClass}`}>{icon}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-slate-900">{title}</h2>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${TONE[state.tone]}`}>
              {state.status}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-600">{body}</p>
          {state.needs ? <p className="mt-1 text-xs font-medium text-amber-900">{state.needs}</p> : null}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">{children}</div>
    </section>
  );
}

function SecondaryButton({ source, onPick }: { source: Source; onPick: (s: Source) => void }) {
  return (
    <button
      type="button"
      disabled={!source.ready}
      aria-disabled={!source.ready}
      onClick={() => onPick(source)}
      className="inline-flex min-h-11 flex-col items-start justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-left text-sm text-slate-800 transition hover:border-cp-blue-500 hover:bg-cp-blue-50 disabled:cursor-not-allowed disabled:opacity-55 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-blue-600"
    >
      <span className="font-semibold">{source.label}</span>
      <span className="text-xs text-slate-500">{source.hint}</span>
    </button>
  );
}

/** Real destinations only: connect, see what was captured, or manage labels. Never "start". */
function MailboxActions({ connected, connectLabel, onOpenConnections, onOpenReview, onOpenCaptureSettings }: {
  connected: boolean; connectLabel: string;
  onOpenConnections?: () => void; onOpenReview?: () => void; onOpenCaptureSettings?: () => void;
}) {
  const link = 'inline-flex min-h-11 items-center gap-1 rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 transition hover:border-cp-blue-500 hover:bg-cp-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-blue-600';
  if (!connected) {
    return onOpenConnections
      ? <button type="button" className={link} onClick={onOpenConnections}>{connectLabel}<ChevronRight /></button>
      : null;
  }
  return (
    <>
      {onOpenReview ? <button type="button" className={link} onClick={onOpenReview}>See captured emails<ChevronRight /></button> : null}
      {onOpenCaptureSettings ? <button type="button" className={link} onClick={onOpenCaptureSettings}>Capture labels<ChevronRight /></button> : null}
    </>
  );
}
